import type { ChatMessage } from '../../../shared/types';
import type { ChatAdapterConfig, ChatProtocolAdapter, ChatAdapterStatus } from './types';
import { generateAnonymousNick, sanitizeNick, senderColor } from './nick';

/**
 * Minimal zero-dependency RFC 1459/2812 client adapter over WebSocket.
 * Connects to either direct WSS or `/api/live/${feedId}/${guid}/irc-bridge`.
 */
export class IrcAdapter implements ChatProtocolAdapter {
  private ws: WebSocket | null = null;
  private channel: string = '';
  private baseNick: string = '';
  private nickAttempt: number = 0;
  private isManuallyClosed: boolean = false;

  private channelUsers: Map<string, { isOp: boolean; isVoice: boolean; modes: string[] }> = new Map();

  currentNick: string = '';
  onMessage: (msg: ChatMessage) => void = () => {};
  onStatus: (status: ChatAdapterStatus, detail?: string) => void = () => {};
  onError: (err: Error) => void = () => {};
  onUserCount?: (count: number) => void;
  onUsers?: (users: import('./types').ChatUser[]) => void;

  async connect(config: ChatAdapterConfig): Promise<void> {
    this.isManuallyClosed = false;

    // Set up channel and nickname
    let ch = config.channel || '';
    if (ch && !ch.startsWith('#') && !ch.startsWith('&')) {
      ch = `#${ch}`;
    }
    this.channel = ch;

    const initialNick = config.nick ? sanitizeNick(config.nick) : generateAnonymousNick();
    this.baseNick = initialNick;
    this.nickAttempt = 0;
    this.currentNick = initialNick;

    // Determine WebSocket endpoint
    let wsUrl: string;
    const server = config.server || '';
    if (server.startsWith('ws://') || server.startsWith('wss://')) {
      wsUrl = server;
    } else {
      // Route via edge bridge: /api/live/${feedId}/${guid}/irc-bridge?server=${server}&port=${port}&channel=${channel}
      const port = config.port || 6697;
      const params = new URLSearchParams();
      if (server) params.set('server', server);
      params.set('port', String(port));
      if (this.channel) params.set('channel', this.channel);

      // In browser or Node/test, derive ws path
      const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = typeof window !== 'undefined' && window.location.host ? window.location.host : 'localhost';
      const basePath = `/api/live/${config.feedId}/${encodeURIComponent(config.guid)}/irc-bridge`;
      wsUrl = `${protocol}//${host}${basePath}?${params.toString()}`;
    }

    this.onStatus('connecting');

    return new Promise<void>((resolve, reject) => {
      try {
        const ws = new WebSocket(wsUrl);
        this.ws = ws;

        let resolved = false;

        ws.onopen = () => {
          this.onStatus('connected');
          if (!resolved) {
            resolved = true;
            resolve();
          }
          // Initial IRC registration commands
          this.sendRaw(`NICK ${this.currentNick}`);
          this.sendRaw(`USER guest 0 * :Podcastie Listener`);
        };

        ws.onmessage = (event: MessageEvent) => {
          const raw = typeof event.data === 'string' ? event.data : '';
          this.handleIncomingData(raw);
        };

        ws.onerror = () => {
          const err = new Error('IRC WebSocket error');
          this.onError(err);
          this.onStatus('error', 'IRC WebSocket error');
          if (!resolved) {
            resolved = true;
            reject(err);
          }
        };

        ws.onclose = () => {
          if (!this.isManuallyClosed) {
            this.onStatus('disconnected');
          }
        };
      } catch (err: any) {
        this.onError(err);
        this.onStatus('error', err?.message || 'Failed to connect');
        reject(err);
      }
    });
  }

  async sendMessage(text: string): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected to IRC');
    }
    if (!this.channel) {
      throw new Error('No IRC channel configured');
    }
    const cleanText = text.replace(/[\r\n]/g, ' ').trim();
    if (!cleanText) return;

    this.sendRaw(`PRIVMSG ${this.channel} :${cleanText}`);

    // Emit local ChatMessage
    const localMsg: ChatMessage = {
      id: `irc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      text: cleanText,
      displayName: this.currentNick,
      timestamp: Date.now(),
      protocol: 'irc',
      senderColor: senderColor(this.currentNick),
    };
    this.onMessage(localMsg);
  }

  async changeNick(newNick: string): Promise<void> {
    const sanitized = sanitizeNick(newNick);
    this.baseNick = sanitized;
    this.nickAttempt = 0;
    this.currentNick = sanitized;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendRaw(`NICK ${this.currentNick}`);
    }
  }

  disconnect(): void {
    this.channelUsers.clear();
    this.emitUsersUpdate();
    this.isManuallyClosed = true;
    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.sendRaw('QUIT :Podcastie disconnected');
        }
        this.ws.close();
      } catch {
        // Ignore error on close
      }
      this.ws = null;
    }
    this.onStatus('disconnected');
  }

  private emitUsersUpdate(): void {
    const users: import('./types').ChatUser[] = Array.from(this.channelUsers.entries()).map(([nick, meta]) => ({
      nick,
      isOp: meta.isOp,
      isVoice: meta.isVoice,
      modes: meta.modes,
    }));

    // Sort: Ops first, then Voiced, then alphabetical
    users.sort((a, b) => {
      if (a.isOp !== b.isOp) return a.isOp ? -1 : 1;
      if (a.isVoice !== b.isVoice) return a.isVoice ? -1 : 1;
      return a.nick.localeCompare(b.nick, undefined, { sensitivity: 'base' });
    });

    if (this.onUserCount) {
      this.onUserCount(Math.max(1, users.length));
    }
    if (this.onUsers) {
      this.onUsers(users);
    }
  }

  private sendRaw(line: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(`${line}\r\n`);
    }
  }

  private handleIncomingData(data: string): void {
    const lines = data.split(/\r?\n/);
    for (const line of lines) {
      if (!line.trim()) continue;
      this.parseIrcLine(line.trim());
    }
  }

  /**
   * Minimal IRC line parser.
   * Format: `[:prefix] <command> [params...] [:trailing]`
   */
  private parseIrcLine(rawLine: string): void {
    let line = rawLine;
    let prefix = '';

    if (line.startsWith(':')) {
      const spaceIdx = line.indexOf(' ');
      if (spaceIdx === -1) return;
      prefix = line.slice(1, spaceIdx);
      line = line.slice(spaceIdx + 1).trimStart();
    }

    let trailing = '';
    const trailingIdx = line.indexOf(' :');
    if (trailingIdx !== -1) {
      trailing = line.slice(trailingIdx + 2);
      line = line.slice(0, trailingIdx).trim();
    }

    const parts = line.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return;

    const command = parts[0].toUpperCase();
    const args = parts.slice(1);
    if (trailingIdx !== -1) {
      args.push(trailing);
    }

    this.handleCommand(prefix, command, args);
  }

  private handleCommand(prefix: string, command: string, args: string[]): void {
    // 1. PING :token -> PONG :token
    if (command === 'PING') {
      const token = args[0] || '';
      this.sendRaw(`PONG :${token}`);
      return;
    }

    // 2. 001 RPL_WELCOME -> JOIN channel
    if (command === '001') {
      if (this.channel) {
        this.sendRaw(`JOIN ${this.channel}`);
      }
      return;
    }

    // 3. 433 ERR_NICKNAMEINUSE -> resolve nick collision
    if (command === '433') {
      this.nickAttempt++;
      const suffix = this.nickAttempt < 10 ? String(this.nickAttempt) : Math.floor(Math.random() * 0x1000).toString(16);
      const maxBaseLen = 16 - suffix.length;
      const base = this.baseNick.slice(0, maxBaseLen);
      this.currentNick = `${base}${suffix}`;
      this.sendRaw(`NICK ${this.currentNick}`);
      return;
    }

    // 4. 353 RPL_NAMREPLY: <clientNick> <symbol> <channel> :<nick1> <nick2> ...
    if (command === '353') {
      // The nick list is in the trailing param (last arg)
      const namesList = args[args.length - 1] || '';
      const rawNicks = namesList.split(/\s+/).filter(Boolean);
      for (const rawNick of rawNicks) {
        // Extract channel status prefixes:
        // @ = op, + = voice, % = halfop, & = admin, ~ = owner, ! = service
        const modeMatch = rawNick.match(/^([@+%&~!]+)/);
        const prefixChars = modeMatch ? modeMatch[1] : '';
        const cleanNick = rawNick.slice(prefixChars.length);
        if (cleanNick) {
          const isOp = prefixChars.includes('@') || prefixChars.includes('&') || prefixChars.includes('~');
          const isVoice = prefixChars.includes('+');
          const modes = prefixChars.split('');
          this.channelUsers.set(cleanNick, { isOp, isVoice, modes });
        }
      }
      return;
    }

    // 5. 366 RPL_ENDOFNAMES: <clientNick> <channel> :End of /NAMES list.
    if (command === '366') {
      this.emitUsersUpdate();
      return;
    }

    // 6. JOIN: :nick!user@host JOIN :#channel
    if (command === 'JOIN') {
      const senderNick = prefix.split('!')[0] || '';
      if (senderNick) {
        if (!this.channelUsers.has(senderNick)) {
          this.channelUsers.set(senderNick, { isOp: false, isVoice: false, modes: [] });
        }
        this.emitUsersUpdate();
      }
      return;
    }

    // 7. PART / KICK: user left or was kicked
    if (command === 'PART' || command === 'KICK') {
      let targetNick = prefix.split('!')[0] || '';
      if (command === 'KICK' && args.length >= 2) {
        targetNick = args[1];
      }
      if (targetNick) {
        this.channelUsers.delete(targetNick);
        this.emitUsersUpdate();
      }
      return;
    }

    // 8. QUIT: user disconnected
    if (command === 'QUIT') {
      const senderNick = prefix.split('!')[0] || '';
      if (senderNick && this.channelUsers.has(senderNick)) {
        this.channelUsers.delete(senderNick);
        this.emitUsersUpdate();
      }
      return;
    }

    // 9. NOTICE - handle server notices, CTCP replies, or user notices
    if (command === 'NOTICE') {
      // Server notices or system messages should NEVER appear as chat messages in the room
      // Server prefixes usually contain '.' and no '!' (e.g. skylark.zeronode.net or *.zeronode.net)
      const isServerNotice = !prefix || (!prefix.includes('!') && prefix.includes('.')) || prefix === '*' || prefix.startsWith('irc.');
      if (isServerNotice) {
        return;
      }
      // If NOTICE is a CTCP reply (e.g. \x01VERSION ...\x01), ignore it
      const text = args.length > 1 ? args[1] : args[0];
      if (text && text.startsWith('\x01')) {
        return;
      }
      // Other notices from servers or services are silenced to prevent noise in user chat
      return;
    }

    // 10. PRIVMSG target :message
    if (command === 'PRIVMSG') {
      // args: [target, message]
      const text = args.length > 1 ? args[1] : args[0];
      // prefix is typically nick!user@host
      const senderNick = prefix.split('!')[0] || 'anonymous';

      // Ignore messages where sender is a server (e.g. skylark.zeronode.net)
      const isServerSender = !prefix.includes('!') && prefix.includes('.');
      if (isServerSender) {
        return;
      }

      // Check for CTCP query: \x01COMMAND [params]\x01 or starting with \x01
      if (text && text.startsWith('\x01')) {
        const ctcpEndIdx = text.endsWith('\x01') ? text.length - 1 : text.indexOf('\x01', 1);
        const ctcpContent = (ctcpEndIdx > 0 ? text.slice(1, ctcpEndIdx) : text.slice(1)).trim();
        const [ctcpCmd, ...ctcpParams] = ctcpContent.split(/\s+/);
        const upperCtcp = (ctcpCmd || '').toUpperCase();

        if (upperCtcp === 'VERSION') {
          // Respond to CTCP VERSION query via NOTICE
          this.sendRaw(`NOTICE ${senderNick} :\x01VERSION Podcastie Web 0.1.0\x01`);
          return;
        }

        if (upperCtcp === 'PING') {
          const pingPayload = ctcpParams.join(' ');
          this.sendRaw(`NOTICE ${senderNick} :\x01PING ${pingPayload}\x01`);
          return;
        }

        if (upperCtcp === 'ACTION') {
          const actionText = ctcpParams.join(' ');
          const msg: ChatMessage = {
            id: `irc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            text: `* ${senderNick} ${actionText}`,
            displayName: senderNick,
            timestamp: Date.now(),
            protocol: 'irc',
            senderColor: senderColor(senderNick),
          };
          this.onMessage(msg);
          return;
        }

        // Ignore any other CTCP command/garbage so control characters or raw queries don't leak into chat
        return;
      }

      const msg: ChatMessage = {
        id: `irc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        text: text || '',
        displayName: senderNick,
        timestamp: Date.now(),
        protocol: 'irc',
        senderColor: senderColor(senderNick),
      };
      this.onMessage(msg);
      return;
    }
  }
}
